import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Images, LoaderCircle, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DELETE_PHOTO_ALBUM, PHOTO_ALBUMS } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import type { PhotoUi } from '../lib/permissions'
import type { PhotoAlbum } from '../types'
import AlbumDialog from './AlbumDialog'
import AlbumsViewSkeleton from './AlbumsViewSkeleton'

interface Props {
  projectId: string
  ui: PhotoUi
  onOpenAlbum: (albumId: string) => void
}

const AlbumsView: React.FC<Props> = ({ projectId, ui, onOpenAlbum }) => {
  const { t } = useTranslation()
  const [editing, setEditing] = useState<PhotoAlbum | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<PhotoAlbum | null>(null)
  const { data, loading } = useQuery<{ photoAlbums: PhotoAlbum[] }>(PHOTO_ALBUMS, { variables: { projectId }, fetchPolicy: 'cache-and-network' })
  const [deleteAlbum, { loading: deletingNow }] = useMutation(DELETE_PHOTO_ALBUM, {
    context: { handlesErrors: true },
    refetchQueries: ['photoAlbums', 'photos'],
  })

  const confirmDelete = async () => {
    if (!deleting) return
    try {
      await deleteAlbum({ variables: { id: deleting.id } })
      setDeleting(null)
    } catch (e: any) {
      toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))
    }
  }

  const albums = data?.photoAlbums ?? []

  return (
    <div className="space-y-3">
      {ui.canManageStructure && (
        <div className="flex justify-end">
          <Button size="sm" className="h-8" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            {t('Photos_album_new')}
          </Button>
        </div>
      )}

      {loading && albums.length === 0 ? (
        <AlbumsViewSkeleton />
      ) : albums.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">{t('Photos_albums_empty')}</div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
          {albums.map((album) => (
            <div key={album.id} className="group relative">
              <button type="button" className="block w-full space-y-2 text-left" onClick={() => onOpenAlbum(album.id)}>
                <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-md bg-muted">
                  {album.coverUrl ? (
                    <img src={album.coverUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
                  ) : (
                    <Images className="h-10 w-10 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <div className="truncate font-medium">{album.name}</div>
                  <div className="text-sm text-muted-foreground">{t('Photos_album_photo_count', { count: album.photoCount })}</div>
                </div>
              </button>
              {(album.canEdit || album.canDelete) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" className="absolute right-1.5 top-1.5 h-8 w-8 p-0 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100" aria-label={t('Photos_album_menu')}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {album.canEdit && (
                      <DropdownMenuItem onSelect={() => setEditing(album)}>
                        <Pencil className="h-4 w-4" />
                        {t('Photos_album_rename')}
                      </DropdownMenuItem>
                    )}
                    {album.canDelete && (
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => setDeleting(album)}>
                        <Trash2 className="h-4 w-4" />
                        {t('Common_delete')}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      )}

      <AlbumDialog projectId={projectId} open={creating} onClose={() => setCreating(false)} />
      <AlbumDialog projectId={projectId} album={editing} open={!!editing} onClose={() => setEditing(null)} />

      <Dialog open={!!deleting} onOpenChange={(o) => !o && !deletingNow && setDeleting(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('Photos_album_delete_title')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('Photos_album_delete_confirm', { name: deleting?.name })}</p>
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button variant="outline" disabled={deletingNow} onClick={() => setDeleting(null)}>{t('Common_cancel')}</Button>
            <Button variant="destructive" disabled={deletingNow} onClick={confirmDelete}>
              {deletingNow && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t('Common_delete')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AlbumsView
